import { inject, Injectable } from '@angular/core';
import {
  LassiApplication,
  VicnamesNamedShape,
  VicnamesShapeType,
} from '@core/api/angular';
import { APPLICATION } from '@lassi/config';
import { GeoJSON } from 'leaflet';
import { Proj4GeoJSONFeature } from 'proj4leaflet';
import { IdentifyFeatureType } from '../feature.model';
import { QuickSearchResult } from '../quick-search.model';
import { RouteUtilsService } from '../route-utils.service';
import {
  bindHighlightLabel,
  createSearchResultGeometryWithLabel,
} from './highlight-label-utils';
import { createSearchResultGeometry } from './map.model';

/** The identify view a highlight label points at. */
export interface HighlightLabelIdentifyTarget {
  type: IdentifyFeatureType.Feature | IdentifyFeatureType.Road;
  id: string | number;
}

/** Everything needed to render (and optionally action) a highlight label. */
export interface HighlightLabelData {
  label: string;
  identify?: HighlightLabelIdentifyTarget;
}

/**
 * Describe the highlight label for a quick search result. Only VICNAMES places
 * and roads are labelled, and both can be identified by their `doc_id`.
 */
export function getQuickSearchHighlightLabel(
  result: QuickSearchResult,
): HighlightLabelData | undefined {
  switch (result.type) {
    case 'road':
      return toHighlightLabel(
        result.name,
        IdentifyFeatureType.Road,
        result.doc_id,
      );
    case 'place':
      return toHighlightLabel(
        result.display_text,
        IdentifyFeatureType.Feature,
        result.doc_id,
      );
    default:
      return undefined;
  }
}

/**
 * Describe the highlight label for a VICNAMES search result shape. The shape
 * carries its own type, so a feature never resolves to a road. FOI features
 * have no id and therefore cannot be identified.
 */
export function getVicnamesShapeHighlightLabel(
  shape: VicnamesNamedShape,
): HighlightLabelData | undefined {
  return toHighlightLabel(
    shape.name,
    shape.type === VicnamesShapeType.ROAD
      ? IdentifyFeatureType.Road
      : IdentifyFeatureType.Feature,
    shape.id,
  );
}

function toHighlightLabel(
  label: string | null | undefined,
  type: HighlightLabelIdentifyTarget['type'],
  id: string | number | null | undefined,
): HighlightLabelData | undefined {
  if (!label) {
    return undefined;
  }
  return id == null || id === ''
    ? { label }
    : { label, identify: { type, id } };
}

/**
 * Builds search result geometries whose highlight label knows where it points.
 * Callers hand over the label data; the redirect itself is owned here.
 */
@Injectable({ providedIn: 'root' })
export class HighlightLabelService {
  private readonly routeUtils = inject(RouteUtilsService);
  /** Only the main LASSI app has identify feature/road routes. */
  private readonly canIdentify = inject(APPLICATION) === LassiApplication.LASSI;

  createGeometry(
    geoJson: Proj4GeoJSONFeature,
    labelData: HighlightLabelData | undefined,
  ) {
    if (!labelData?.label) {
      return createSearchResultGeometry(geoJson);
    }
    return createSearchResultGeometryWithLabel(
      geoJson,
      labelData.label,
      this.createClickHandler(labelData.identify),
    );
  }

  /** Label an existing highlight layer, e.g. one created by the map service. */
  bindLabel(layer: GeoJSON, labelData: HighlightLabelData | undefined) {
    if (!labelData?.label) {
      return;
    }
    bindHighlightLabel(
      layer,
      labelData.label,
      this.createClickHandler(labelData.identify),
    );
  }

  createQuickSearchGeometry(
    result: QuickSearchResult,
    geoJson: Proj4GeoJSONFeature,
  ) {
    return this.createGeometry(geoJson, getQuickSearchHighlightLabel(result));
  }

  createVicnamesShapeGeometry(shape: VicnamesNamedShape) {
    return this.createGeometry(
      shape.shape as Proj4GeoJSONFeature,
      getVicnamesShapeHighlightLabel(shape),
    );
  }

  private createClickHandler(
    identify: HighlightLabelIdentifyTarget | undefined,
  ) {
    if (!identify || !this.canIdentify) {
      return undefined;
    }
    return () => this.routeUtils.goToIdentify(identify.type, identify.id);
  }
}
